import React, { FC, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';

import {
  OPENAI_GENERATE_DESCRIPTION,
  OPENAI_DESCRIPTION_VAR
} from 'graphql/queries/openai/openai.query';
import { DESCRIPTION_EVENT_TYPE, DESCRIPTION_TONE } from 'constants/ai-prompt.constants';

import EventTypePromptModal from './components/EventTypePromptModal';
import EventDescriptionToneModal from './components/EventDescriptionToneModal';
import EventDescriptionLength from './components/EventDescriptionLength';
import EventDescriptionComments from './components/EventDescriptionComments';
import EventDescriptionConfirm from './components/EventDescriptionConfirm';

enum DESCRIPTION_GENERATE_MODAL_ENUMS {
  EVENT_TYPE = 'event_type',
  TONE = 'tone',
  TEXT_LENGTH = 'length',
  LAST_COMMENT = 'last_comment',
  CONFIRM_GENERATED = 'confirm'
}

interface EventDescriptionPromptWrapperProps {
  shouldBeginStep: boolean;
  eventTitle: string;
  resetStep: () => void;
  submitDescription: (description: string) => void;
  showToastError: () => void;
}

/**
 * Wrapper to handle opening up each step modals to generate description.
 *
 * @param param0
 * @returns
 */
const EventDescriptionPromptWrapper: FC<
  React.PropsWithChildren<EventDescriptionPromptWrapperProps>
> = ({ shouldBeginStep, eventTitle, submitDescription, resetStep, showToastError }) => {
  const [eventTypes, setEventTypes] = useState<string[]>();
  const [eventTypeOther, setEventTypeOther] = useState<string>();

  const [tones, setTones] = useState<string[]>();
  const [toneOther, setToneOther] = useState<string>();

  const [length, setLength] = useState<string>();
  const [comment, setComment] = useState<string>();

  const [description, setDescription] = useState<string>('');

  const [modalOpenEnum, setModalOpenEnum] = useState<DESCRIPTION_GENERATE_MODAL_ENUMS>();

  const [getAI] = useLazyQuery<
    { generatedDescription: { answer: string } },
    OPENAI_DESCRIPTION_VAR
  >(OPENAI_GENERATE_DESCRIPTION, {
    fetchPolicy: 'network-only',
    onError: () => {
      handleModalClose();
      showToastError();
    }
  });

  useEffect(() => {
    if (shouldBeginStep) {
      setModalOpenEnum(DESCRIPTION_GENERATE_MODAL_ENUMS.EVENT_TYPE);
    }
  }, [shouldBeginStep]);

  /**
   * Close out this modal wrapper as whole.
   * Reset model open enum state value.
   */
  const handleModalClose = (): void => {
    setEventTypes([]);
    setTones([]);
    setLength('');
    setComment('');
    setDescription('');

    resetStep();
    setModalOpenEnum(undefined);
  };

  const handleAI = async (commentTemp: string): Promise<void> => {
    if (!eventTypes || eventTypes.length === 0 || !tones || tones.length === 0 || !length) {
      return;
    }

    // Set correct 'event_type' to send.
    let eventTypeOtherInput;
    if (eventTypes.includes(DESCRIPTION_EVENT_TYPE.OTHER) && eventTypeOther) {
      eventTypeOtherInput = eventTypeOther;
    }

    // Set correct 'tone' to send.

    let toneOtherInput;
    if (tones.includes(DESCRIPTION_TONE.OTHER) && toneOther) {
      toneOtherInput = toneOther;
    }

    const result = await getAI({
      variables: {
        eventTypes,
        eventTypeOtherInput,
        tones,
        toneOtherInput,
        length,
        eventTitle,
        comment: commentTemp || ''
      }
    });

    if (result?.data?.generatedDescription) {
      setDescription(result?.data?.generatedDescription.answer);
    }
  };

  return (
    <>
      {/* step 1: Asking for type of event */}
      <EventTypePromptModal
        open={modalOpenEnum === DESCRIPTION_GENERATE_MODAL_ENUMS.EVENT_TYPE}
        onOpenChange={(openTemp: boolean): void => {
          if (!openTemp) {
            handleModalClose();
          }
        }}
        handleSubmit={(typesTemp: string[], typeOtherTemp?: string): void => {
          setEventTypes(typesTemp);
          if (typesTemp.includes(DESCRIPTION_EVENT_TYPE.OTHER) && typeOtherTemp) {
            setEventTypeOther(typeOtherTemp);
          } else {
            setEventTypeOther('');
          }

          setModalOpenEnum(DESCRIPTION_GENERATE_MODAL_ENUMS.TONE);
        }}
        selectedTypes={eventTypes}
        otherInput={eventTypeOther}
      />

      {/* step 2: Asking for tone of description */}
      <EventDescriptionToneModal
        open={modalOpenEnum === DESCRIPTION_GENERATE_MODAL_ENUMS.TONE}
        onOpenChange={(openTemp: boolean): void => {
          if (!openTemp) {
            handleModalClose();
          }
        }}
        handleSubmit={(toneTemp: string[], toneOtherTemp?: string): void => {
          setTones(toneTemp);

          if (toneTemp.includes(DESCRIPTION_EVENT_TYPE.OTHER) && toneOtherTemp) {
            setToneOther(toneOtherTemp);
          } else {
            setToneOther('');
          }

          setModalOpenEnum(DESCRIPTION_GENERATE_MODAL_ENUMS.TEXT_LENGTH);
        }}
        handleBack={(): void => {
          setModalOpenEnum(DESCRIPTION_GENERATE_MODAL_ENUMS.EVENT_TYPE);
        }}
        selectedTones={tones}
        otherInput={toneOther}
      />

      {/* Step 3: Asking for length of description */}
      <EventDescriptionLength
        open={modalOpenEnum === DESCRIPTION_GENERATE_MODAL_ENUMS.TEXT_LENGTH}
        onOpenChange={(openTemp: boolean): void => {
          if (!openTemp) {
            handleModalClose();
          }
        }}
        handleSubmit={(lengthTemp: string): void => {
          setLength(lengthTemp);
          setModalOpenEnum(DESCRIPTION_GENERATE_MODAL_ENUMS.LAST_COMMENT);
        }}
        handleBack={(): void => {
          setModalOpenEnum(DESCRIPTION_GENERATE_MODAL_ENUMS.TONE);
        }}
        selectedLength={length}
      />

      {/* Step 4: Ask for any other data */}
      <EventDescriptionComments
        open={modalOpenEnum === DESCRIPTION_GENERATE_MODAL_ENUMS.LAST_COMMENT}
        onOpenChange={(openTemp: boolean): void => {
          if (!openTemp) {
            handleModalClose();
          }
        }}
        handleSubmit={(commentTemp: string): void => {
          setComment(commentTemp);
          handleAI(commentTemp);

          setModalOpenEnum(DESCRIPTION_GENERATE_MODAL_ENUMS.CONFIRM_GENERATED);
        }}
        handleBack={(): void => {
          setModalOpenEnum(DESCRIPTION_GENERATE_MODAL_ENUMS.TEXT_LENGTH);
        }}
        commentInput={comment}
      />

      {/* Step 5: Confirm description */}
      <EventDescriptionConfirm
        open={modalOpenEnum === DESCRIPTION_GENERATE_MODAL_ENUMS.CONFIRM_GENERATED}
        onOpenChange={(openTemp: boolean): void => {
          if (!openTemp) {
            handleModalClose();
          }
        }}
        handleSubmit={(): void => {
          submitDescription(description);
          handleModalClose();
        }}
        handleBack={(): void => {
          setDescription('');
          setModalOpenEnum(DESCRIPTION_GENERATE_MODAL_ENUMS.LAST_COMMENT);
        }}
        description={description}
      />
    </>
  );
};

export default EventDescriptionPromptWrapper;
